import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartContext } from '../../context/CartContext';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import { ArrowLeft, CreditCard, Truck, ShieldCheck, MapPin, Phone, User, Mail, Plus, Check } from 'lucide-react';
import apiService from '../../components/services/apiService';
import { formatVND } from '../../utils/formatters';
import { validateEmail, validatePhone } from '../../utils/validators';

export default function CheckoutPage() {
  const { cart, subtotal, discountAmount, shippingFee, finalTotal, clearCart } = useCartContext();
  const { user } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]); 
  const [selectedAddressId, setSelectedAddressId] = useState(null); 
  const [isAddingNew, setIsAddingNew] = useState(false); 

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    note: '',
    paymentMethod: 'COD',
  });

  const [errors, setErrors] = useState({});

  // 1. Tải danh sách địa chỉ từ API /api/v1/users/addresses (khớp với ProfilePage)
  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        fullName: user.fullName || user.name || '',
        email: user.email || '',
        phone: user.phoneNumber || user.phone || '',
      }));

      const fetchAddresses = async () => {
        try {
          const response = await apiService.get('/users/addresses');
          const addresses = response.data || response;
          
          if (Array.isArray(addresses) && addresses.length > 0) {
            setSavedAddresses(addresses);
            // Ưu tiên chọn địa chỉ mặc định, nếu không có lấy địa chỉ đầu tiên
            const defaultAddr = addresses.find((a) => a.isDefault) || addresses[0];
            setSelectedAddressId(defaultAddr.id);
            
            // Format gộp chuỗi địa chỉ khớp với cấu trúc trong ProfilePage
            const fullStr = `${defaultAddr.streetAddress}, ${defaultAddr.city}`;
            setFormData((prev) => ({
              ...prev,
              address: fullStr,
              phone: defaultAddr.phone || prev.phone,
              fullName: defaultAddr.recipientName || prev.fullName,
            }));
          } else {
            setIsAddingNew(true); // Chưa có địa chỉ thì bật form nhập mới
          }
        } catch (error) {
          console.error('Không thể tải danh sách địa chỉ:', error);
          setIsAddingNew(true);
        }
      };

      fetchAddresses();
    }
  }, [user]);

  // Xử lý khi chọn một địa chỉ có sẵn trong danh sách
  const handleSelectAddress = (addr) => {
    setSelectedAddressId(addr.id);
    setIsAddingNew(false);
    const fullStr = `${addr.streetAddress}, ${addr.city}`;
    setFormData((prev) => ({
      ...prev,
      address: fullStr,
      phone: addr.phone || prev.phone,
      fullName: addr.recipientName || prev.fullName,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Vui lòng nhập họ tên';
    if (!formData.email.trim() || !validateEmail(formData.email)) newErrors.email = 'Email không hợp lệ';
    if (!formData.phone.trim() || !validatePhone(formData.phone)) newErrors.phone = 'Số điện thoại không hợp lệ';
    if (!formData.address.trim()) newErrors.address = 'Vui lòng nhập địa chỉ nhận hàng';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (!user) {
      showToast('Vui lòng đăng nhập để thanh toán', 'warning');
      return;
    }

    if (cart.length === 0) {
      showToast('Giỏ hàng của bạn đang trống', 'warning');
      return;
    }

    if (!validateForm()) {
      showToast('Vui lòng kiểm tra lại thông tin giao hàng', 'error');
      return;
    }

    setLoading(true);
    try {
      let finalAddressStr = formData.address;

      // NẾU ĐANG Ở CHẾ ĐỘ NHẬP MỚI HOẶC CHƯA CÓ ĐỊA CHỈ NÀO TRONG SỔ:
      // Lưu địa chỉ này vào cơ sở dữ liệu trước rồi mới lấy chuỗi/ID đó đặt hàng
      if (isAddingNew || savedAddresses.length === 0) {
        try {
          const addressPayload = {
            recipientName: formData.fullName,
            phone: formData.phone,
            streetAddress: formData.address, // Hoặc tách riêng số nhà / thành phố tùy theo API của bạn
            city: 'Chưa cập nhật', // Hoặc lấy từ input thành phố nếu có
            isDefault: savedAddresses.length === 0 ? true : false // Nếu chưa có thì set mặc định luôn
          };

          const addrRes = await apiService.post('/users/addresses', addressPayload);
          const newAddrData = addrRes.data || addrRes;
          
          if (newAddrData && newAddrData.streetAddress) {
            finalAddressStr = `${newAddrData.streetAddress}, ${newAddrData.city || ''}`;
          }
        } catch (addrError) {
          console.error('Không thể tự động lưu địa chỉ mới:', addrError);
          // Vẫn cho phép tiếp tục dùng chuỗi formData.address nhập tay nếu API địa chỉ lỗi
        }
      }
      const orderPayload = {
        userId: user.id,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address, 
        note: formData.note,
        paymentMethod: formData.paymentMethod || 'COD',
        shippingFee: shippingFee || 0,
        totalAmount: subtotal,
        finalAmount: finalTotal,
        items: cart.map((item) => ({
          productId: item.productId || item.id,
          productVariantId: item.productVariantId || item.variantId || item.selectedVariantId,
          productName: item.name || item.productName,
          image: item.image || item.images || item.thumbnail,
          quantity: item.quantity,
          price: item.price,
          color: item.selectedColor || 'default',
          size: item.selectedSize || 'default',
        })),
      };

      if (formData.paymentMethod === 'VNPAY') {
        const response = await apiService.post('/payment/create-vnpay', orderPayload);
        const data = response.data || response;

        if (data.success && data.paymentUrl) {
          await clearCart();
          window.location.href = data.paymentUrl;
        } else {
          showToast(data.message || 'Không thể tạo liên kết thanh toán VNPay', 'error');
          setLoading(false);
        }
      } else {
        const response = await apiService.post('/orders/create', orderPayload);
        const data = response.data || response;

        if (data.success === false) {
          showToast(data.message || 'Đặt hàng thất bại', 'error');
        } else {
          showToast(data.message || 'Đặt hàng thành công!', 'success');
          await clearCart();
          navigate('/orders');
        }
        setLoading(false);
      }
    } catch (error) {
      console.error('Lỗi khi đặt hàng:', error);
      showToast('Không thể kết nối đến server để đặt hàng', 'error');
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center space-y-5">
        <h2 className="text-xl font-extrabold text-slate-900">Yêu cầu đăng nhập</h2>
        <button onClick={() => navigate('/')} className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold">
          Quay lại trang chủ
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      <div>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 hover:text-indigo-600 bg-white border border-slate-200 px-3.5 py-2 rounded-xl transition shadow-sm cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4" /> Quay lại
        </button>
      </div>

      <h1 className="text-2xl font-extrabold text-slate-900">Thanh Toán Đơn Hàng</h1>

      <form onSubmit={handleSubmitOrder} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-extrabold text-slate-900 flex items-center justify-between border-b border-slate-100 pb-3">
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-600" /> Địa chỉ nhận hàng
              </span>
              {savedAddresses.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsAddingNew(!isAddingNew)}
                  className="text-xs text-indigo-600 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" /> {isAddingNew ? 'Chọn từ sổ địa chỉ' : 'Nhập địa chỉ khác'}
                </button>
              )}
            </h3>

            {/* Hiển thị danh sách địa chỉ chuẩn từ bảng addresses */}
            {!isAddingNew && savedAddresses.length > 0 ? (
              <div className="space-y-3">
                {savedAddresses.map((addr) => {
                  const isSelected = selectedAddressId === addr.id;
                  return (
                    <div
                      key={addr.id}
                      onClick={() => handleSelectAddress(addr)}
                      className={`p-4 rounded-2xl border cursor-pointer transition flex items-start justify-between ${
                        isSelected ? 'border-indigo-600 bg-indigo-50/30' : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="space-y-1 text-xs">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-slate-900">{addr.recipientName}</span>
                          <span className="text-slate-400">|</span>
                          <span className="text-slate-600">{addr.phone}</span>
                          {addr.isDefault && (
                            <span className="bg-indigo-100 text-indigo-700 text-[10px] px-2 py-0.5 rounded-md font-bold">Mặc định</span>
                          )}
                        </div>
                        <p className="text-slate-600">
                          {addr.streetAddress}, {addr.city}
                        </p>
                      </div>
                      <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300'}`}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Form nhập mới nếu chưa có địa chỉ hoặc người dùng muốn nhập địa chỉ tạm thời */
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Họ và tên người nhận</label>
                  <div className="relative">
                    <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      placeholder="Nhập họ và tên"
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500"
                    />
                  </div>
                  {errors.fullName && <p className="text-[11px] text-rose-500 mt-1">{errors.fullName}</p>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Email</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="example@gmail.com"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    {errors.email && <p className="text-[11px] text-rose-500 mt-1">{errors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">Số điện thoại</label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="0901234567"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500"
                      />
                    </div>
                    {errors.phone && <p className="text-[11px] text-rose-500 mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Địa chỉ nhận hàng chi tiết</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Số nhà, tên đường, phường/xã, quận/huyện, thành phố"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500"
                  />
                  {errors.address && <p className="text-[11px] text-rose-500 mt-1">{errors.address}</p>}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1 mt-3">Ghi chú đơn hàng (Tùy chọn)</label>
              <textarea
                name="note"
                value={formData.note}
                onChange={handleChange}
                rows="2"
                placeholder="Lưu ý cho đơn vị vận chuyển..."
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500 resize-none"
              />
            </div>
          </div>

          {/* Phương thức thanh toán */}
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-extrabold text-slate-900 flex items-center gap-2 border-b border-slate-100 pb-3">
              <CreditCard className="w-4 h-4 text-indigo-600" /> Phương thức thanh toán
            </h3>

            <div className="space-y-3">
              <label className={`flex items-center justify-between p-4 rounded-2xl border cursor-pointer transition ${formData.paymentMethod === 'COD' ? 'border-indigo-600 bg-indigo-50/30' : 'border-slate-200 hover:bg-slate-50'}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="COD"
                    checked={formData.paymentMethod === 'COD'}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-xs font-bold text-slate-900 block">Thanh toán khi nhận hàng (COD)</span>
                    <span className="text-[11px] text-slate-500">Thanh toán tiền mặt trực tiếp cho nhân viên giao hàng.</span>
                  </div>
                </div>
                <Truck className="w-5 h-5 text-indigo-600" />
              </label>

              <label className={`flex items-center justify-between p-4 rounded-2xl border cursor-pointer transition ${formData.paymentMethod === 'VNPAY' ? 'border-indigo-600 bg-indigo-50/30' : 'border-slate-200 hover:bg-slate-50'}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="VNPAY"
                    checked={formData.paymentMethod === 'VNPAY'}
                    onChange={handleChange}
                    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <span className="text-xs font-bold text-slate-900 block">Thanh toán trực tuyến qua VNPay</span>
                    <span className="text-[11px] text-slate-500">Thẻ ATM, Visa, MasterCard qua cổng VNPay.</span>
                  </div>
                </div>
                <CreditCard className="w-5 h-5 text-indigo-600" />
              </label>
            </div>
          </div>
        </div>

        {/* Cột phải: Tóm tắt đơn hàng */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-sm space-y-4 sticky top-6">
            <h3 className="text-sm font-extrabold text-slate-900 border-b border-slate-100 pb-3">
              Đơn hàng của bạn ({cart.length} sản phẩm)
            </h3>

            <div className="divide-y divide-slate-100 max-h-64 overflow-y-auto pr-1">
              {cart.map((item, index) => (
                <div key={index} className="py-2.5 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-2.5">
                    <img src={item.images ? item.images[0] : item.image} alt={item.name} className="w-10 h-10 rounded-lg object-cover border border-slate-200 shrink-0" />
                    <div>
                      <p className="font-bold text-slate-800 line-clamp-1">{item.name}</p>
                      <p className="text-[10px] text-slate-500">SL: {item.quantity} | {item.selectedColor} - {item.selectedSize}</p>
                    </div>
                  </div>
                  <span className="font-bold text-slate-900 shrink-0">{formatVND(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2 pt-3 border-t border-slate-100 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>Tạm tính</span>
                <span className="font-bold text-slate-900">{formatVND(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span>Giảm giá</span>
                  <span className="font-bold">-{formatVND(discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Phí vận chuyển</span>
                <span className="font-bold text-slate-900">{shippingFee === 0 ? 'Miễn phí' : formatVND(shippingFee)}</span>
              </div>
              <div className="flex justify-between text-sm pt-3 border-t border-slate-100 font-extrabold text-slate-900">
                <span>Tổng thanh toán</span>
                <span className="text-indigo-600">{formatVND(finalTotal)}</span>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition shadow-lg shadow-indigo-600/20 flex items-center justify-center space-x-2 disabled:opacity-50 cursor-pointer"
            >
              {loading ? 'Đang xử lý...' : (formData.paymentMethod === 'VNPAY' ? 'Thanh toán qua VNPay' : 'Đặt Hàng Ngay (COD)')}
            </button>

            <div className="flex items-center justify-center gap-2 text-[11px] text-slate-400 pt-2">
              <ShieldCheck className="w-4 h-4 text-emerald-500" /> Cam kết bảo mật thông tin & chính hãng từ Nhật Bản
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}